'use client';

import { useEffect, useId, useRef, useState } from 'react';

import { routing } from '~/lib/navigation';
import { Skeleton } from '~/components/ui/skeleton';
import { Spinner } from '~/components/ui/spinner';
import { Trans } from '~/components/ui/trans';

import { LookupSourceGroupsSkeleton, PropertyLoadingShell } from './property-loading-shell';

export function PropertyNavigationPending({
  target,
  locale,
  onRestore,
}: {
  target: string;
  locale: string;
  onRestore: () => void;
}) {
  const headingId = useId();
  const heading = useRef<HTMLHeadingElement>(null);
  const [slow, setSlow] = useState(false);
  const home = locale === routing.defaultLocale ? '/' : `/${locale}`;

  // The initiating combobox unmounts during the handoff. Move keyboard focus
  // to this new surface, allow it to paint, then use a native navigation.
  // Reset transient state if browser Back restores this document from bfcache.
  useEffect(() => {
    heading.current?.focus();
    const slowTimer = setTimeout(() => setSlow(true), 8000);
    const navigationTimer = setTimeout(() => window.location.assign(target), 100);
    const resetRestoredPage = (event: PageTransitionEvent) => {
      if (event.persisted) onRestore();
    };
    window.addEventListener('pageshow', resetRestoredPage);

    return () => {
      clearTimeout(slowTimer);
      clearTimeout(navigationTimer);
      window.removeEventListener('pageshow', resetRestoredPage);
    };
  }, [onRestore, target]);

  return (
    <div className="container" data-test="property-navigation-pending">
      <PropertyLoadingShell
        opening={
          <section className="pw-lookup-progress" aria-labelledby={headingId}>
            <div className="pw-lookup-progress-heading">
              <span className="pw-lookup-progress-icon" aria-hidden="true">
                <Spinner className="size-5" />
              </span>
              <div className="pw-lookup-progress-copy">
                <h2 id={headingId} ref={heading} tabIndex={-1} className="outline-none">
                  <Trans i18nKey="parcelWatch.navigation.openingTitle" />
                </h2>
                <p role="status" aria-atomic="true" data-test="property-navigation-status">
                  <Trans
                    i18nKey={
                      slow
                        ? 'parcelWatch.navigation.slowDescription'
                        : 'parcelWatch.navigation.openingDescription'
                    }
                  />
                </p>
              </div>
              <Skeleton className="h-3 w-14 shrink-0" aria-hidden="true" />
            </div>
            <LookupSourceGroupsSkeleton />
            <div className="pw-lookup-progress-foot">
              <p>
                <Trans i18nKey="parcelWatch.navigation.recoveryDescription" />
              </p>
              <div className="pw-navigation-recovery">
                {/* These links let users retry or leave a slow document load. */}
                <a href={target} data-test="property-navigation-retry">
                  <Trans i18nKey="parcelWatch.navigation.openDirectly" />
                </a>
                <a href={home} data-test="property-navigation-home">
                  <Trans i18nKey="parcelWatch.navigation.searchAgain" />
                </a>
              </div>
            </div>
            <div className="pw-lookup-source-details" aria-hidden="true">
              <Skeleton className="my-3 h-3.5 w-44" />
            </div>
          </section>
        }
      />
    </div>
  );
}
